import React, { useState, useEffect } from 'react';
import { cefrDescriptors } from '../data/questions';
import { calculateSkillBreakdown, getRecommendations, getMonthsToNextLevel } from '../utils/scoring';

export default function ResultPage({ finalLevel, allAnswers, allQuestions, onRestart }) {
  const [showDetails, setShowDetails] = useState(false);
  const descriptor = cefrDescriptors[finalLevel];
  const skillBreakdown = calculateSkillBreakdown(allAnswers, allQuestions);
  const recommendations = getRecommendations(finalLevel, skillBreakdown);
  const nextLevelInfo = getMonthsToNextLevel(finalLevel);
  
  useEffect(() => {
    setTimeout(() => setShowDetails(true), 500);
  }, []);
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-8 text-white text-center">
            <div className="animate-fade-in">
              <h1 className="text-4xl font-bold mb-2">Test Complete!</h1>
              <p className="text-blue-100 text-lg">Your English proficiency results</p>
            </div>
          </div>
          
          <div className="p-8">
            <div className="text-center mb-8 animate-slide-up">
              <div className="inline-block bg-gradient-to-r from-blue-500 to-indigo-500 text-white px-8 py-4 rounded-full text-3xl font-bold mb-4 shadow-lg">
                {descriptor.level}
              </div>
              <p className="text-gray-700 text-lg max-w-2xl mx-auto mt-4">
                {descriptor.description}
              </p>
            </div>
            
            <div className={`grid md:grid-cols-2 gap-6 mb-8 transition-opacity duration-1000 ${showDetails ? 'opacity-100' : 'opacity-0'}`}>
              <div className="bg-blue-50 p-6 rounded-xl border border-blue-200">
                <h3 className="font-semibold text-lg mb-2 text-blue-900">IELTS Equivalent</h3>
                <p className="text-3xl font-bold text-blue-600">{descriptor.ielts}</p>
              </div>
              
              <div className="bg-indigo-50 p-6 rounded-xl border border-indigo-200">
                <h3 className="font-semibold text-lg mb-2 text-indigo-900">TOEFL Equivalent</h3>
                <p className="text-3xl font-bold text-indigo-600">{descriptor.toefl}</p>
              </div>
            </div>
            
            {skillBreakdown.length > 0 && (
              <div className={`mb-8 transition-opacity duration-1000 delay-300 ${showDetails ? 'opacity-100' : 'opacity-0'}`}>
                <h3 className="font-semibold text-xl mb-4 text-gray-900">Skills Breakdown</h3>
                <div className="space-y-4">
                  {skillBreakdown.map((skill, index) => (
                    <div key={index}>
                      <div className="flex justify-between mb-2">
                        <span className="font-medium text-gray-700 capitalize">{skill.skill}</span>
                        <span className="text-gray-600">{skill.correct}/{skill.total} ({skill.percentage}%)</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-3">
                        <div
                          className={`h-3 rounded-full transition-all duration-1000 ${
                            skill.percentage >= 80 ? 'bg-green-500' :
                            skill.percentage >= 60 ? 'bg-yellow-500' :
                            'bg-red-500'
                          }`}
                          style={{ width: `${skill.percentage}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {recommendations.length > 0 && (
              <div className={`mb-8 transition-opacity duration-1000 delay-500 ${showDetails ? 'opacity-100' : 'opacity-0'}`}>
                <h3 className="font-semibold text-xl mb-4 text-gray-900">Recommendations</h3>
                <div className="space-y-4">
                  {recommendations.map((rec, index) => (
                    <div key={index} className="bg-gray-50 p-5 rounded-lg border border-gray-200">
                      <h4 className="font-semibold text-gray-900 mb-2">{rec.area}</h4>
                      <ul className="list-disc list-inside mb-2 text-gray-700">
                        {rec.items.map((item, i) => (
                          <li key={i}>{item}</li>
                        ))}
                      </ul>
                      <p className="text-sm text-gray-600 italic">{rec.suggestion}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {nextLevelInfo && (
              <div className={`mb-8 bg-gradient-to-r from-amber-50 to-orange-50 p-6 rounded-xl border-2 border-amber-300 transition-opacity duration-1000 delay-700 ${showDetails ? 'opacity-100' : 'opacity-0'}`}>
                <div className="flex items-start space-x-4">
                  <div className="text-3xl">📚</div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg text-amber-900 mb-2">Path to Next Level</h3>
                    <p className="text-gray-700 mb-3">
                      With regular practice, you can reach <span className="font-bold text-amber-900">{nextLevelInfo.nextLevel}</span> in approximately <span className="font-bold text-amber-900">{nextLevelInfo.months} months</span>.
                    </p>
                    <button
                      onClick={() => window.location.href = '/lms-dashboard.html'}
                      className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-6 py-2 rounded-lg font-semibold hover:from-amber-600 hover:to-orange-600 transition-all inline-block"
                    >
                      📅 Book a Lesson Now
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            <div className="text-center">
              <button
                onClick={onRestart}
                className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-8 py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all transform hover:scale-105 shadow-lg"
              >
                Take Test Again
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
